import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './app';
import { ToastProvider } from './shell/toast';
import { applyTheme, storedTheme } from './lib/theme';
import { ApiFailure } from './lib/result';
import { captureInvite } from './workspaces/workspace-form';
import { upgradeLegacyHash } from './shell/use-route';
import './styles.css';

/**
 * Fleet data is polled, not pushed: a sync runs every few minutes, so a stale
 * screen is measured in seconds and refetching on focus is enough. A 4xx is an
 * answer, not a blip, so only network failures and 5xx are retried, once.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: (failures, error) =>
        failures < 1 && !(error instanceof ApiFailure && (error.status ?? 500) < 500),
    },
  },
});

// Old `#/…` links first, then an emailed invitation, kept until the person is signed in.
upgradeLegacyHash();
captureInvite();

// Before the first paint, so a light-theme user never sees a dark flash.
applyTheme(storedTheme());

const root = document.getElementById('root');
if (root === null) throw new Error('index.html is missing #root');

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <App />
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);
